import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PEDashboard from "@/pages/PEDashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import PEProfileEditor from "@/pages/PEProfileEditor";
import AdminApprovalsPage from "@/pages/AdminApprovalsPage";
import AdminPELicensesPage from "@/pages/AdminPELicensesPage";
import AdminPEAnalyticsPage from "@/pages/AdminPEAnalyticsPage";
import AdminRefundsPage from "@/pages/AdminRefundsPage";
import AdminVisitorsPage from "@/pages/AdminVisitorsPage";
import AdminCampaignDetailPage from "@/pages/AdminCampaignDetailPage";
import AdminContactInboxPage from "@/pages/AdminContactInboxPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import PaymentHistoryPage from "@/pages/PaymentHistoryPage";
import PEPayoutsPage from "@/pages/PEPayoutsPage";
import DirectoryPage from "@/pages/DirectoryPage";
import DirectoryPEDetail from "@/pages/DirectoryPEDetail";
import ProjectsHubPage from "@/pages/ProjectsHubPage";
import JobsPage from "@/pages/JobsPage";
import InboxPage from "@/pages/InboxPage";
import JobNewPage from "@/pages/JobNewPage";
import JobDetailPage from "@/pages/JobDetailPage";
import StudiesPage from "@/pages/StudiesPage";
import ProjectBuilder from "@/pages/ProjectBuilder";
import Library from "@/pages/Library";
import Deliverables from "@/pages/Deliverables";
import JobDeliverablesPage from "@/pages/JobDeliverablesPage";
import Settings from "@/pages/Settings";
import CalculatorsPage from "@/pages/CalculatorsPage";
import OrgPage from "@/pages/OrgPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminUserTransactionsPage from "@/pages/AdminUserTransactionsPage";
import AdminPermissionsPage from "@/pages/AdminPermissionsPage";
import AdminIntegrationsPage from "@/pages/AdminIntegrationsPage";
import AdminVendorInquiriesPage from "@/pages/AdminVendorInquiriesPage";
import PortalPage from "@/pages/PortalPage";
import StyleGuide from "@/pages/StyleGuide";
import SharedProjectView from "@/pages/SharedProjectView";
import UnderDevelopment from "@/pages/UnderDevelopment";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import ScrollKeeper from "@/components/ScrollKeeper";
import VisitTracker from "@/components/VisitTracker";
import {
  isUnderDevelopment,
  consumeBypassParamIfPresent,
} from "@/lib/appMode";

// One-time bypass capture — allows `?bypass=<token>` on a "live" domain
// to unlock the real app for that browser without redeploying.
consumeBypassParamIfPresent();

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0C10] text-gray-500 font-mono text-sm">
        Initializing studio…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/* v2.88 · Role-gate wrapper. Redirects to /dashboard when the current
   user's role is not in `roles`. Use for admin-only Studio routes. */
function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0C10] text-gray-500 font-mono text-sm">
        Initializing studio…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

/* v2.88 · Dashboard router — dispatches to the role-appropriate landing. */
function DashboardRouter() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0C10] text-gray-500 font-mono text-sm">
        Initializing studio…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Dashboard />;
  if (user.role === "pe") return <PEDashboard />;
  return <ClientDashboard />;
}

export default function App() {
  // Live-deployment gate — flip render tree at the earliest possible
  // point so absolutely nothing from the real app boots on production.
  if (isUnderDevelopment()) {
    return (
      <div className="App">
        <UnderDevelopment />
      </div>
    );
  }
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" theme="dark" toastOptions={{ style: { background: "#12151C", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", borderRadius: 0 } }} />
          <ScrollKeeper />
          <VisitTracker />
          <EmailVerifyBanner />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/portal/:pid/:token" element={<PortalPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify" element={<VerifyEmailPage />} />
            <Route path="/dashboard" element={<DashboardRouter />} />
            {/* v2.90 · "Projects" nav is now the collaboration hub —
                engagements between clients and PEs. The CAD projects
                list moves to admin-only `/studio`. */}
            <Route path="/projects" element={<Protected><ProjectsHubPage /></Protected>} />
            <Route path="/studio" element={<RequireRole roles={["admin"]}><Dashboard /></RequireRole>} />
            <Route path="/projects/:id" element={<Protected><ProjectBuilder /></Protected>} />
            <Route path="/library" element={<Protected><Library /></Protected>} />
            <Route path="/deliverables" element={<Protected><Deliverables /></Protected>} />
            <Route path="/exchange" element={<Protected><JobDeliverablesPage /></Protected>} />
            <Route path="/calculators" element={<RequireRole roles={["admin"]}><CalculatorsPage /></RequireRole>} />
            <Route path="/tools" element={<RequireRole roles={["admin"]}><CalculatorsPage /></RequireRole>} />
            {/* v2.90 · Marketplace surfaces. */}
            <Route path="/pe/profile" element={<RequireRole roles={["pe"]}><PEProfileEditor /></RequireRole>} />
            <Route path="/admin/pe-approvals" element={<RequireRole roles={["admin"]}><AdminApprovalsPage /></RequireRole>} />
            <Route path="/admin/pe-licenses" element={<RequireRole roles={["admin"]}><AdminPELicensesPage /></RequireRole>} />
            <Route path="/admin/pe-analytics" element={<RequireRole roles={["admin"]}><AdminPEAnalyticsPage /></RequireRole>} />
            <Route path="/admin/visitors" element={<RequireRole roles={["admin"]}><AdminVisitorsPage /></RequireRole>} />
            <Route path="/admin/visitors/campaign/:name" element={<RequireRole roles={["admin"]}><AdminCampaignDetailPage /></RequireRole>} />
            <Route path="/admin/contact" element={<RequireRole roles={["admin"]}><AdminContactInboxPage /></RequireRole>} />
            <Route path="/admin/refunds" element={<RequireRole roles={["admin"]}><AdminRefundsPage /></RequireRole>} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/payments" element={<Protected><PaymentHistoryPage /></Protected>} />
            <Route path="/pe/payouts" element={<RequireRole roles={["pe"]}><PEPayoutsPage /></RequireRole>} />
            <Route path="/directory" element={<Protected><DirectoryPage /></Protected>} />
            <Route path="/directory/pes/:id" element={<Protected><DirectoryPEDetail /></Protected>} />
            {/* v2.91 · Jobs · Proposals · Messages. */}
            <Route path="/jobs" element={<Protected><JobsPage /></Protected>} />
            <Route path="/inbox" element={<RequireRole roles={["pe","client"]}><InboxPage /></RequireRole>} />
            <Route path="/jobs/new" element={<RequireRole roles={["client","admin"]}><JobNewPage /></RequireRole>} />
            <Route path="/jobs/:id" element={<Protected><JobDetailPage /></Protected>} />
            {/* v2.92 · Power System Studies MVP. */}
            {/* v3.2.24 · Studies is admin-only. */}
            <Route path="/studies" element={<RequireRole roles={["admin"]}><StudiesPage /></RequireRole>} />
            {/* Admin-only surfaces. */}
            <Route path="/orgs" element={<RequireRole roles={["admin"]}><OrgPage /></RequireRole>} />
            <Route path="/admin/users" element={<RequireRole roles={["admin"]}><AdminUsersPage /></RequireRole>} />
            <Route path="/admin/users/:userId/transactions" element={<RequireRole roles={["admin"]}><AdminUserTransactionsPage /></RequireRole>} />
            <Route path="/admin/permissions" element={<RequireRole roles={["admin"]}><AdminPermissionsPage /></RequireRole>} />
            <Route path="/admin/integrations" element={<RequireRole roles={["admin"]}><AdminIntegrationsPage /></RequireRole>} />
            <Route path="/admin/vendor-inquiries" element={<RequireRole roles={["admin"]}><AdminVendorInquiriesPage /></RequireRole>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/style-guide" element={<StyleGuide />} />
            <Route path="/share/:token" element={<SharedProjectView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
